/**
 * entities/notification/api/useMarkRead — Mutation hooks for marking notifications read.
 *
 * useMarkReadMutation: POST /api/notifications/{id}/mark-read/
 * useMarkAllReadMutation: POST /api/notifications/mark-all-read/
 *
 * Both mutations invalidate the notifications list and unread-count cache on success,
 * so the badge and list update immediately after a mark-read action. The lifts
 * list is also invalidated so LiftCard's "Neue Updates" badge (which folds in
 * unread notification counts) refreshes.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/api';
import type { components } from '@/shared/api';
import { LIFTS_QUERY_KEY } from '@/entities/lift';
import { NOTIFICATIONS_QUERY_KEY } from './useNotifications';
import { UNREAD_COUNT_QUERY_KEY } from './useUnreadCount';
import type { Notification } from '../model/types';

/**
 * Mark a single notification as read.
 * Returns the updated Notification on success.
 * Invalidates the notifications list and unread-count cache.
 */
export function useMarkReadMutation() {
  const queryClient = useQueryClient();

  return useMutation<Notification, Error, string>({
    mutationFn: async (id: string): Promise<Notification> => {
      const result = await (
        apiClient.POST as (
          p: string,
          opts?: Record<string, unknown>,
        ) => Promise<{ data?: unknown; error?: unknown; response: Response }>
      )(`/api/notifications/${id}/mark-read/`);

      if (result.error !== undefined) {
        const err = result.error;
        const detail =
          typeof err === 'object' && err !== null && 'detail' in err
            ? String((err as Record<string, unknown>)['detail'])
            : `API error: ${result.response.status}`;
        throw new Error(detail);
      }

      return result.data as Notification;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: LIFTS_QUERY_KEY });
    },
  });
}

/**
 * Mark all notifications as read.
 * Returns the updated unread count (should be 0).
 * Invalidates the notifications list and unread-count cache.
 */
export function useMarkAllReadMutation() {
  const queryClient = useQueryClient();

  return useMutation<components['schemas']['UnreadCount'], Error, void>({
    mutationFn: async (): Promise<components['schemas']['UnreadCount']> => {
      const result = await (
        apiClient.POST as (
          p: string,
          opts?: Record<string, unknown>,
        ) => Promise<{ data?: unknown; error?: unknown; response: Response }>
      )('/api/notifications/mark-all-read/');

      if (result.error !== undefined) {
        const err = result.error;
        const detail =
          typeof err === 'object' && err !== null && 'detail' in err
            ? String((err as Record<string, unknown>)['detail'])
            : `API error: ${result.response.status}`;
        throw new Error(detail);
      }

      return (result.data ?? { unread_count: 0 }) as components['schemas']['UnreadCount'];
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: LIFTS_QUERY_KEY });
    },
  });
}
